import { SEVERITY_LABELS } from '@fabric-ui/components';
import { FilterTypes, mapAliasesAndMetrics } from '@fabric-ui/utils';
import { UseQueryOptions } from '@tanstack/react-query';
import { EntitiesLabels } from '@utils/entities.utils';
import { EntityTypeConfigKeys, entityViewConfig } from '@utils/entityViewConfig';
import { field } from '@utils/filter';
import { addMinutes, format, isValid } from 'date-fns';
import { DwQueryRequest } from '../CustomDashboards/types';

export const formatAsUTC = (value, formatWithTime = false, keepSlaWithoutFormat = false) => {
  const utcTime = addMinutes(new Date(value), new Date(value).getTimezoneOffset());
  const date = new Date(value);
  return formatWithTime
    ? (keepSlaWithoutFormat ? date : utcTime).toLocaleString()
    : format(keepSlaWithoutFormat ? date : utcTime, 'MMM dd, yyyy');
};

export const getFieldLabel = ({ field, fieldMap, projectionName, aggProjs }) =>
  fieldMap[field]
    ? fieldMap[field].group === aggProjs[projectionName]?.projDisplayName
      ? fieldMap[field].displayName
      : fieldMap[field].title
    : field;

export const actionCallback =
  (onSuccess, queryClient) =>
  (...args) => {
    onSuccess(...args);
    queryClient.resetQueries({ queryKey: ['ticket-info'] });
  };

export const StepsEnum = {
  NONE: 0,
  NEW: 5,
  DISCOVERED: 6,
  OPENED: 10,
  OPEN: 10,
  REOPEN: 11,
  ACKNOWLEDGED: 20,
  IN_PROGRESS: 21,
  REMEDIATED: 30,
  DISPUTED: 40,
  NOT_CONFIRMED: 50,
  CONFIRMED: 60,
  AUTO_CLOSED: 61,
  FALSE_POSITIVE: 62,
  RISK_ACCEPTED: 63,
};

export const STATUS_LABELS = {
  [StepsEnum.NONE]: 'None',
  [StepsEnum.NEW]: 'New',
  [StepsEnum.DISCOVERED]: 'Discovered',
  [StepsEnum.OPENED]: 'Opened',
  [StepsEnum.OPEN]: 'Open',
  [StepsEnum.REOPEN]: 'Reopen',
  [StepsEnum.ACKNOWLEDGED]: 'Acknowledged',
  [StepsEnum.IN_PROGRESS]: 'In Progress',
  [StepsEnum.REMEDIATED]: 'Remediated',
  [StepsEnum.NOT_CONFIRMED]: 'Not Confirmed',
  [StepsEnum.CONFIRMED]: 'Confirmed',
  [StepsEnum.AUTO_CLOSED]: 'Auto Closed',
  [StepsEnum.FALSE_POSITIVE]: 'False Positive',
  [StepsEnum.RISK_ACCEPTED]: 'Risk Accepted',
};

export const ORDERED_TICKET_STATUS = [
  STATUS_LABELS[StepsEnum.NEW],
  STATUS_LABELS[StepsEnum.OPENED],
  STATUS_LABELS[StepsEnum.OPEN],
  STATUS_LABELS[StepsEnum.REOPEN],
  STATUS_LABELS[StepsEnum.ACKNOWLEDGED],
  STATUS_LABELS[StepsEnum.IN_PROGRESS],
  STATUS_LABELS[StepsEnum.REMEDIATED],
  STATUS_LABELS[StepsEnum.CONFIRMED],
  STATUS_LABELS[StepsEnum.NOT_CONFIRMED],
  STATUS_LABELS[StepsEnum.AUTO_CLOSED],
  STATUS_LABELS[StepsEnum.FALSE_POSITIVE],
  STATUS_LABELS[StepsEnum.RISK_ACCEPTED],
];

export const explodeFields = () => ({
  [FilterTypes.AssetV2]: { select: 'asset._key', group: 'asset._key' },
  [FilterTypes.AssetTypeV2]: { select: 'asset.type', group: 'asset.type' },
  [FilterTypes.TagsV2]: { select: 'EXPLODE(ticket.tags) as tag', group: 'tag' },
});

export const drawerWidth = 1100;

const sortedSeverities = [
  SEVERITY_LABELS.CRITICAL,
  SEVERITY_LABELS.HIGH,
  SEVERITY_LABELS.MEDIUM,
  SEVERITY_LABELS.LOW,
  SEVERITY_LABELS.INFO,
];

export const fieldPurposeMap = {
  // TODO change to field names
  uber_findings: {
    sources: 'Sources',
    type: 'Type',
    title: 'Title',
    severity: 'Severity',
    score: 'Severity score',
    state: 'State',
  },
  uber_assets: {
    sources: 'Sources',
    type: 'Type',
    title: 'Name',
    score: 'Risk Score',
    state: 'State',
  },
  tickets: {
    sources: 'Sources',
    type: 'Type',
    title: 'Title',
    severity: 'Severity',
    score: 'Severity score',
    state: 'State',
  },
  uber_global_vulnerabilities: {
    sources: 'Sources',
    type: 'Type',
    title: 'CVE',
    score: 'CVSS 3.1 Base Score',
    state: 'State',
  },
  uber_org_entities: {
    sources: 'Sources',
    type: 'Type',
    title: 'Name',
    state: 'State',
  },
  uber_components: {
    sources: 'Sources',
    type: 'Type',
    title: 'Name',
    state: 'State',
  },
  uber_component_instances: {
    sources: 'Sources',
    type: 'Type',
    title: 'ID',
    state: 'State',
  },
  uber_alerts: {
    sources: 'Sources',
    type: 'Type',
    title: 'Title',
    severity: 'Severity',
    score: 'Severity Score',
    state: 'State',
  },
  incidents: {
    sources: 'Sources',
    type: 'Type',
    title: 'Title',
    severity: 'Severity',
    score: 'Severity Score',
    state: 'State',
  },
  uber_alert_actors: {
    sources: 'Sources',
    type: 'Type',
    title: 'Title',
    state: 'State',
  },
  uber_applications: {
    sources: 'Sources',
    type: 'Type',
    title: 'Name',
    state: 'State',
  },
  uber_users: {
    sources: 'Sources',
    type: 'Type',
    title: 'Email',
    state: 'State',
  },
  uber_mitre_attacks: {
    sources: 'Sources',
    type: 'Type',
    title: 'Tactic',
    state: 'State',
  },
  default: {
    sources: 'Sources',
    type: 'Type',
    title: 'Name',
    state: 'State',
  },
};

export const groupByKeys = {
  assignee: {
    Ticket: 'ticket.assignee_id',
    Incident: 'incident.assignee_id',
    Asset: 'asset.owner_id',
    sort: ({ title: a }, { title: b }) => {
      const keyToSortFirst = 'No Assignee';
      if ((a === keyToSortFirst) !== (b === keyToSortFirst)) {
        return a === keyToSortFirst ? -1 : 1;
      }
      return a > b ? 1 : a < b ? -1 : 0;
    },
  },
  sla: {
    Ticket: 'ticket.sla',
    Incident: 'incident.sla',
    sort: ({ title: a }, { title: b }) => {
      const keyToSortFirst = 'No SLA';
      if ((a === keyToSortFirst) !== (b === keyToSortFirst)) {
        return a === keyToSortFirst ? 1 : -1;
      }
      return +new Date(a) > +new Date(b) ? 1 : +new Date(a) < +new Date(b) ? -1 : 0;
    },
    formatter: v => (isValid(new Date(v)) ? formatAsUTC(v) : 'No SLA'),
  },
  avalorStatus: {
    Ticket: 'ticket.current_status.name',
    Incident: 'incident.current_status.name',
    sort: ({ title: a }, { title: b }) => {
      const statuses = ORDERED_TICKET_STATUS.map(v => v);
      return statuses.indexOf(a) - statuses.indexOf(b);
    },
    formatter: v => STATUS_LABELS[StepsEnum[v]],
  },
  firstSeen: {
    Ticket: 'ticket.first_seen',
    Asset: 'asset.first_seen',
    sort: ({ title: a }, { title: b }) => (+new Date(a) > +new Date(b) ? 1 : +new Date(a) < +new Date(b) ? -1 : 0),
    formatter: v => (v ? format(new Date(v), 'MMM dd, yyyy') : 'Unknown'),
  },
  created: {
    Ticket: 'ticket.created',
    Incident: 'incident.created',
    Asset: 'asset.created',
    sort: ({ title: a }, { title: b }) => (+new Date(a) > +new Date(b) ? 1 : +new Date(a) < +new Date(b) ? -1 : 0),
    formatter: v => (v ? format(new Date(v), 'MMM dd, yyyy') : 'Unknown'),
  },
  severity: {
    Ticket: 'ticket.severity',
    Incident: 'incident.severity',
    sort: ({ title: a }, { title: b }) => sortedSeverities.indexOf(a) - sortedSeverities.indexOf(b),
    formatter: v => SEVERITY_LABELS[v],
  },
  type: {
    Ticket: 'ticket.type',
    Incident: 'incident.type',
    Asset: 'asset.type',
    sort: ({ title: a }, { title: b }) => a - b,
  },
};

export const groupByKeysQueryProjection = entityTypeId => ({
  [groupByKeys.assignee?.[entityTypeId]]: `${groupByKeys.assignee?.[entityTypeId]} as val`,
  [groupByKeys.sla?.[entityTypeId]]: `DAY(${groupByKeys.sla?.[entityTypeId]}) AS val`,
  [groupByKeys.type?.[entityTypeId]]: `${groupByKeys.type?.[entityTypeId]} as val`,
  [groupByKeys.avalorStatus?.[entityTypeId]]: `${groupByKeys.avalorStatus?.[entityTypeId]} as val`,
  ...(entityTypeId === entityViewConfig.Ticket.entityTypeId
    ? { [groupByKeys.firstSeen?.[entityTypeId]]: `DAY(${groupByKeys.firstSeen?.[entityTypeId]}) as val` }
    : {}),
});

type SqlStringParams = {
  fields: string[];
  headCellsFields?: string[];
  where?: string;
  isExport?: boolean;
  metrics?: { systemName: string }[];
};
export const baseTicketSql = ({ fields = [], where = '', isExport = false, metrics = [] }: SqlStringParams) => `select  
                  ${
                    isExport
                      ? fields.join(', ')
                      : `ticket.created as created
                  ${fields.includes('ticket._key') ? '' : ', ticket._key'} 
                  ${fields.includes('ticket.type') ? '' : ', ticket.type'} 
                  ${fields.includes('ticket.integration_info.key') ? '' : ', ticket.integration_info.key'} 
                  ${!fields.includes('ticket.state') ? ', ticket.state' : ''} 
                  ${fields.length ? `, ${fields.join(', ')}` : ''}`
                  }
                 ${fields.length && metrics?.length ? ', ' : ''} ${metrics
                   ?.map(({ systemName }) => `${systemName} as ${systemName}`)
                   .join(', ')}
                                                                                                        from tickets 
                  ${where}`;

export const baseIncidentSql = ({ fields = [], where = '', isExport = false, metrics = [] }: SqlStringParams) => `select 
                ${
                  isExport
                    ? fields.join(', ')
                    : `incident.created as created
                ${fields.includes('incident._key') ? '' : ', incident._key'} 
                ${fields.includes('incident.type') ? '' : ', incident.type'} 
                ${fields.includes('incident.integration_info.key') ? '' : ', incident.integration_info.key'} 
                ${fields.length ? `, ${fields.join(', ')}` : ''}`
                }
                  ${fields.length && metrics?.length ? ', ' : ''} ${metrics
                    ?.map(({ systemName }) => `${systemName} as ${systemName}`)
                    .join(', ')}
                from incidents
                ${where}`;

export const baseIncidentThreatsSql = ({ fields = [], where = '', isExport = false, metrics = [] }: SqlStringParams) => `select 
                ${
                  isExport
                    ? fields.join(', ')
                    : `incident.created as created
                ${fields.includes('incident._key') ? '' : ', incident._key'} 
                ${fields.includes('incident.type') ? '' : ', incident.type'} 
                ${fields.includes('incident.integration_info.key') ? '' : ', incident.integration_info.key'} 
                ${fields.length ? `, ${fields.join(', ')}` : ''}`
                }
                  ${fields.length && metrics?.length ? ', ' : ''} ${metrics
                    ?.map(({ systemName }) => `${systemName} as ${systemName}`)
                    .join(', ')}
                from incidents
                ${where}`;

export const baseAssetSql = ({ fields = [], where = '', headCellsFields = [], metrics = [] }: SqlStringParams) => `select
                asset.created as created,
                ${mapAliasesAndMetrics.severityScore.metric} as ${mapAliasesAndMetrics.severityScore.alias},
                ${mapAliasesAndMetrics.totalFindings.metric} as ${mapAliasesAndMetrics.totalFindings.alias},
                critical_active_findings_granular AS CRITICAL,
                high_active_findings_granular AS HIGH,
                medium_active_findings_granular AS MEDIUM,
                low_active_findings_granular AS LOW,
                info_active_findings_granular AS NONE
                ${fields.includes('asset._key') ? '' : ', asset._key'}
                ${fields.length ? `, ${fields.join(', ')}` : ''}
                ${fields.length && metrics?.length ? ', ' : ''} ${metrics
                  ?.map(({ systemName }) => `${systemName} as ${systemName}`)
                  .join(', ')}
                from uber_assets
                ${where}
                group by created
                ${headCellsFields.includes('asset._key') ? '' : ', asset._key'}
                ${headCellsFields.includes('asset.type') ? '' : ', asset.type'}
                ${headCellsFields.length ? `, ${headCellsFields.join(', ')}` : ''}`;

export const baseAlertSql = ({ fields = [], where = '', isExport = false, metrics = [] }: SqlStringParams) => `select
                ${
                  isExport
                    ? fields.join(', ')
                    : `
                alert.created as created,
                alert.severity as severity,
                alert.grouping_key as grouping_key
                ${fields.includes('alert._key') ? '' : ', alert._key'} 
                ${fields.includes('alert.type') ? '' : ', alert.type'} 
                ${!fields.includes('alert.state') ? ', alert.state' : ''} 
                ${fields.length ? `, ${fields.join(', ')}` : ''}`
                }
                ${fields.length && metrics.length ? ', ' : ''} ${metrics
                  .map(({ systemName }) => `${systemName} as ${systemName}`)
                  .join(', ')}
                from uber_alerts 
                ${where}`;
export const baseAlertThreatsSql = ({ fields = [], where = '', isExport = false, metrics = [] }: SqlStringParams) => `select
               ${
                 isExport
                   ? fields.join(', ')
                   : `
                alert.created as created,
                alert.severity as severity,
                alert.original_severity as original_severity,
                alert.grouping_key as grouping_key
                ${fields.includes('alert._key') ? '' : ', alert._key'} 
                ${fields.includes('alert.type') ? '' : ', alert.type'} 
                ${!fields.includes('alert.state') ? ', alert.state' : ''} 
                ${fields.length ? `, ${fields.join(', ')}` : ''}`
               }
               ${fields.length && metrics.length ? ', ' : ''} ${metrics
                 .map(({ systemName }) => `${systemName} as ${systemName}`)
                 .join(', ')}
                from uber_alerts
                ${where}`;
export const baseFindingSql = ({ fields = [], where = '', isExport = false, metrics = [] }: SqlStringParams) => `select
            ${
              isExport
                ? fields.join(', ')
                : `
            finding.created as created
            ${fields.includes('finding._key') ? '' : ', finding._key'}
            ${fields.includes('finding.type') ? '' : ', finding.type'} 
            ${!fields.includes('finding.state') ? ', finding.state' : ''} 
            ${fields.length ? `, ${fields.join(', ')}` : ''}`
            }
            ${fields.length && metrics?.length ? ', ' : ''} ${metrics?.map(({ systemName }) => `${systemName} as ${systemName}`).join(', ')}
            from uber_findings
           ${where}`;

export const baseFactorSql = ({ fields = [], where = '', isExport = false }: SqlStringParams) => `select
           ${
             isExport
               ? fields.join(', ')
               : `
               factor.created as created, 
               factor.type as type, 
               factor.group_id as groupId, 
               factor.effective_normalized_score as scoreLeft,
               factor_normalized_max_score_granular as scoreRight
                ${fields.includes('factor._key') ? '' : ', factor._key'} 
                ${fields.length ? `, ${fields.join(', ')}` : ''}`
           }  from uber_factors
         ${where}`;

export const basePolicyViolationSql = ({ fields = [], where = '', isExport = false }: SqlStringParams) => `select
           ${
             isExport
               ? fields.join(', ')
               : `
               policy_violation.created as created, 
               policy_violation.type as type,
               policy_violation.severity
               ${fields.includes('policy_violation._key') ? '' : ', policy_violation._key'} 
               ${fields.length ? `, ${fields.join(', ')}` : ''}`
           }  from uber_policy_violations
         ${where}`;

export const baseViolationTicketSql = ({ fields = [], where = '', isExport = false }: SqlStringParams) => `select
           ${
             isExport
               ? fields.join(', ')
               : `
               violation_ticket.created as created, 
               violation_ticket.type as type,
               violation_ticket.severity
               ${fields.includes('violation_ticket.integration_info.key') ? '' : ', violation_ticket.integration_info.key'},
               violation_remediation_percentage_granular as violation_remediation_percentage_granular,
               total_assets_granular as total_assets_granular,
               total_policy_violations_granular as total_policy_violations_granular
               ${fields.length ? `, ${fields.join(', ')}` : ''}`
           }  from violation_tickets
         ${where}`;

export const baseExceptionsSql = ({ fields = [], where = '', isExport = false }: SqlStringParams) => `select
           ${
             isExport
               ? fields.join(', ')
               : `
               exception.created as created
                ${fields.includes('exception._key') ? '' : ', exception._key'} 
                ${fields.length ? `, ${fields.join(', ')}` : ''}
                ${fields.includes('exception.integration_info.key') ? '' : ', exception.integration_info.key'}`
           }  from uber_exceptions
         ${where}`;

export const baseIdentityFindingsSql = ({ fields = [], where = '', isExport = false }: SqlStringParams) => `select
            ${
              isExport
                ? fields.join(', ')
                : `
            identity_finding._key,
            identity_finding.severity as severity,
            identity_finding.title as title,
            COLLECT_DISTINCT(mitre_attack.tactic) as mitreTactics,
            COLLECT_DISTINCT(mitre_attack.technique) as mitreTechniques,
            tenant.name as tenantName,
            affected_identities_identity_findings as ${EntitiesLabels.IDENTITIES},
            affected_assets_identity_findings as ${EntitiesLabels.ASSETS},
            affected_groups_identity_findings as ${EntitiesLabels.GROUPS},
            affected_tenants_identity_findings as ${EntitiesLabels.TENANTS}
             ${
               fields.length
                 ? `, 
             ${fields.join(', ')}`
                 : ''
             }`
            }  from uber_identity_findings
          ${where}`;

export const baseTenantSql = ({ fields = [], where = '', isExport = false }: SqlStringParams) => `select ${
  isExport
    ? fields.join(', ')
    : `
tenant._key,
tenant.severity_score,
tenant.severity,
total_identity_findings as totalFindings,
critical_active_identity_findings as CRITICAL,
high_active_identity_findings as HIGH,
medium_active_identity_findings as MEDIUM,
low_active_identity_findings as LOW ${fields.length ? `, ${fields.join(', ')}` : ''}`
}
                                                      from uber_tenants ${where}`;

export const getInitialCustomHeadCells = ({ projectionName, extraRelationGroups, aggProjs, getType, isTableV2 }) => {
  const filteredFieldList = extraRelationGroups?.includes('*')
    ? aggProjs[projectionName].fieldList.INTERACTIVE
    : aggProjs[projectionName].fieldList.INTERACTIVE.filter(({ group }) => extraRelationGroups.includes(group));
  return filteredFieldList.map(({ name, value, title }) => {
    if (isTableV2) {
      return {
        id: value,
        header: title || name,
        meta: {
          hidden: true,
          type: getType(name),
        },
      };
    }
    return {
      id: value,
      label: title || name,
      hidden: true,
      type: getType(name),
    };
  });
};

export const baseIdentitiesSql = ({ fields = [], where = '', isExport = false }: SqlStringParams) => `select
            ${
              isExport
                ? fields.join(', ')
                : `user._key,
            total_identity_findings as totalFindings,
            critical_active_identity_findings as CRITICAL,
            high_active_identity_findings as HIGH,
            medium_active_identity_findings as MEDIUM,
            low_active_identity_findings as LOW ${fields.length ? `, ${fields.join(', ')}` : ''}`
            }  from uber_users
          ${where}`;

// TODO: move baseEntitySqlsMap to entityViewConfig
// should change once old incident page will be removed
export const getBaseEntitySqlsMap = (isThreatApp = false) => ({
  [entityViewConfig.Ticket.entityTypeId]: baseTicketSql,
  [entityViewConfig.Incident.entityTypeId]: isThreatApp ? baseIncidentThreatsSql : baseIncidentSql,
  [entityViewConfig.Asset.entityTypeId]: baseAssetSql,
  [entityViewConfig.Alert.entityTypeId]: isThreatApp ? baseAlertThreatsSql : baseAlertSql,
  [entityViewConfig.Finding.entityTypeId]: baseFindingSql,
  [entityViewConfig.Factor.entityTypeId]: baseFactorSql,
  [entityViewConfig.PolicyViolation.entityTypeId]: basePolicyViolationSql,
  [entityViewConfig.ViolationTicket.entityTypeId]: baseViolationTicketSql,
  [entityViewConfig.Exception.entityTypeId]: baseExceptionsSql,
  [entityViewConfig.Tenant.entityTypeId]: baseTenantSql,
  [entityViewConfig.IdentityFinding.entityTypeId]: baseIdentityFindingsSql,
  [entityViewConfig.User.entityTypeId]: baseIdentitiesSql,
});

type RiskMassQueryConfig = {
  options: UseQueryOptions;
  queryObject: DwQueryRequest;
  onSuccess: (data: any) => any;
};

type GetRiskMassQueryObjectFn = ({
  ticketKey,
  entityPathAlias,
  projectionId,
}: {
  ticketKey: string;
  entityPathAlias: string;
  projectionId: { name: string; builtIn: boolean };
}) => RiskMassQueryConfig;
type ProjectionName = (typeof entityViewConfig)[EntityTypeConfigKeys]['projectionName'];

const getRiskMassQueryObject = ({ ticketKey, projectionId, entityPathAlias }): RiskMassQueryConfig => ({
  options: { queryKey: ticketKey, enabled: !!ticketKey, gcTime: 0 },
  queryObject: {
    projectionId,
    select: {
      dims: [
        { name: 'low_active_findings_risk_mass_granular', alias: 'low_active_findings_risk_mass_granular' },
        { name: 'medium_active_findings_risk_mass_granular', alias: 'medium_active_findings_risk_mass_granular' },
        { name: 'high_active_findings_risk_mass_granular', alias: 'high_active_findings_risk_mass_granular' },
        { name: 'critical_active_findings_risk_mass_granular', alias: 'critical_active_findings_risk_mass_granular' },
        { name: 'active_findings_risk_mass_granular', alias: 'active_findings_risk_mass_granular' },
      ],
      metrics: [],
    },
    filter: field(`${entityPathAlias}._key`).str.equals(ticketKey),
    sorting: [],
    groupBy: [],
  },
  onSuccess: d => d[0],
});

export const projectionRiskMassMap: Partial<Record<ProjectionName, GetRiskMassQueryObjectFn>> = {
  [entityViewConfig.Ticket.projectionName]: ({ ticketKey, projectionId, entityPathAlias }) =>
    getRiskMassQueryObject({ ticketKey, projectionId, entityPathAlias }),
  [entityViewConfig.Asset.projectionName]: ({ ticketKey, projectionId, entityPathAlias }) =>
    getRiskMassQueryObject({ ticketKey, projectionId, entityPathAlias }),
};

const bulkPrefix = 'notification/ticket/bulk/';
export const apiUrls = {
  UPDATE_INTEGRATION: `${bulkPrefix}integration`,
  UPDATE_FIELDS: `${bulkPrefix}multi-fields`,
  CREATE_SINGLE_INTEGRATION: 'notification/ticket/integration',
  ADD_ATTACHMENT: 'notification/entity/metadata/attachment',
  UNLINK_INTEGRATION: 'notification/ticket/integration/unlink',
};

export const statusField = 'current_status.name';
export const filtersFieldToIgnore = [
  'tablePage',
  'orderBy',
  'search',
  'searchArray',
  'groupByKey',
  'tab',
  'ticketCategory',
  'ticket.detection_sources',
  'viewId',
  'activeEntityId',
  'investigationMode',
  'headCells',
  'expandCharts',
  'date',
  'dataSelectTypes',
  'fieldType',
  'isEditWidgetOpen',
  'isEditMode',
  'isCurrentTime',
  'activeProjName',
  'timeRange',
  'forceRender',
  'expressions',
  'dateGap',
  'dateObject',
];
export const staticFiltersFieldsV2 = ['is_fixable', 'current_status.name', 'severity', 'severity', 'source_names'];
export const getNestedFieldObj = (field, value) => ({ [field]: { name: value, timestamp: new Date().toISOString() } });

export const TABLE_AVALOR_KEY = 'avalor_key';
export const ASSET_TABLE_AVALOR_KEY = 'asset_avalor_key';
export const SCORE_EXPLANATION_FIELD = 'finding.score_factors';
export const FINDING_SEVERITY = 'finding.severity';
export const POLICY_VIOLATION_SEVERITY = 'policy_violation.severity';
export const POLICY_VIOLATION_SEVERITY_SCORE = 'policy_violation.severity_score';
export const ALERT_SEVERITY = 'alert.severity';
export const FINDING_SEVERITY_SCORE = 'finding.severity_score';
export const ALERT_SEVERITY_SCORE = 'alert.severity_score';
export const FINDING_ORIGINAL_SEVERITY = 'finding.original_severity';
export const FINDING_ORIGINAL_SEVERITYSCORE = 'finding.original_severity_score';

export const EXCEPTION_REQUESTER_EMAIL = 'exception.requester_email';
export const EXCEPTION_REQUESTER_NAME = 'requester.name';
export const EXCEPTION_REVIEWER_EMAIL = 'exception.reviewer_email';
export const EXCEPTION_REVIEWER_NAME = 'reviewer.name';
export const EXCEPTION_STATUS_BUCKET_NAME = 'exception.status.status_bucket.name';
export const EXCEPTION_JUSTIFICATION_NAME = 'justification';
export const EXCEPTION_REASON_NAME = 'reason';
export const EXCEPTION_REQUESTED_SLA_NAME = 'requested_sla';

export const USER_NAME = "COALESCE(user.first_name + ' ' + user.last_name, user.user_name)";

export const SELECTED_ALL_ROWS_DISABLED_MSG = 'This action is disabled when all rows are selected';
export const fieldsWithEmptyStateSet = new Set(['alert.score_factors']);
